import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldHalved,
  faBars,
  faXmark,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import { personalInfo } from "../data/portfolioData";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);

      const sections = ["home", "about", "experience", "projects", "architecture", "skills", "certifications", "contact"];
      const scrollPosition = window.scrollY + 120;

      for (const section of sections) {
        const el = document.getElementById(section);
        if (el) {
          const top = el.offsetTop;
          const height = el.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "About", href: "#about", id: "about" },
    { name: "Experience", href: "#experience", id: "experience" },
    { name: "Projects", href: "#projects", id: "projects" },
    { name: "Architecture", href: "#architecture", id: "architecture" },
    { name: "Skills", href: "#skills", id: "skills" },
    { name: "Research", href: "#certifications", id: "certifications" },
    { name: "Contact", href: "#contact", id: "contact" },
  ];

  const closeMobile = () => setMobileMenuOpen(false);

  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 1000,
        backgroundColor: scrolled ? "rgba(8, 10, 13, 0.88)" : "rgba(8, 10, 13, 0.6)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        borderBottom: `1px solid ${scrolled ? "rgba(31, 38, 51, 0.9)" : "transparent"}`,
        transition: "all 0.3s ease",
      }}
    >
      <div className="container d-flex align-items-center justify-content-between py-3">
        {/* Brand */}
        <a
          href="#home"
          className="d-flex align-items-center gap-2 text-decoration-none"
          style={{ color: "var(--text-primary)" }}
        >
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: "8px",
              background: "linear-gradient(135deg, rgba(56, 189, 248, 0.2), rgba(129, 140, 248, 0.1))",
              border: "1px solid rgba(56, 189, 248, 0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--accent)",
            }}
          >
            <FontAwesomeIcon icon={faShieldHalved} style={{ fontSize: "0.95rem" }} />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: "1.05rem", letterSpacing: "-0.01em", lineHeight: 1.2 }}>
              {personalInfo.name}
            </div>
            <div
              className="font-mono d-none d-sm-block"
              style={{ fontSize: "0.7rem", color: "var(--accent)", opacity: 0.9 }}
            >
              SECURITY ANALYST
            </div>
          </div>
        </a>

        {/* Desktop Navigation */}
        <nav className="d-none d-lg-flex align-items-center gap-1">
          {navLinks.map((link) => {
            const isActive = activeSection === link.id;
            return (
              <a
                key={link.id}
                href={link.href}
                className="text-decoration-none px-3 py-1"
                style={{
                  color: isActive ? "var(--accent)" : "var(--text-secondary)",
                  fontSize: "0.9rem",
                  fontWeight: isActive ? 600 : 500,
                  transition: "var(--transition)",
                  position: "relative",
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.color = "var(--text-primary)";
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.color = "var(--text-secondary)";
                }}
              >
                {link.name}
                {isActive && (
                  <span
                    style={{
                      position: "absolute",
                      bottom: -2,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: "16px",
                      height: "2px",
                      backgroundColor: "var(--accent)",
                      borderRadius: "1px",
                      boxShadow: "0 0 8px var(--accent)",
                    }}
                  />
                )}
              </a>
            );
          })}
        </nav>

        {/* Desktop CTA */}
        <div className="d-none d-lg-flex align-items-center gap-3">
          <a
            href="#contact"
            className="btn-cyber-primary"
            style={{ padding: "8px 16px", fontSize: "0.85rem" }}
          >
            Get In Touch
            <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: "0.75rem" }} />
          </a>
        </div>

        {/* Mobile Hamburger Button */}
        <button
          className="d-lg-none"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          style={{
            background: "transparent",
            border: "1px solid var(--border-subtle)",
            borderRadius: "6px",
            color: "var(--text-primary)",
            padding: "8px 12px",
            cursor: "pointer",
          }}
        >
          <FontAwesomeIcon icon={mobileMenuOpen ? faXmark : faBars} style={{ fontSize: "1.1rem" }} />
        </button>
      </div>

      {/* Mobile Drawer */}
      {mobileMenuOpen && (
        <div
          className="d-lg-none"
          style={{
            backgroundColor: "rgba(14, 17, 22, 0.98)",
            borderBottom: "1px solid var(--border-subtle)",
            padding: "20px 24px",
          }}
        >
          <div className="d-flex flex-column gap-3">
            {navLinks.map((link) => (
              <a
                key={link.id}
                href={link.href}
                onClick={closeMobile}
                className="text-decoration-none py-2"
                style={{
                  color: activeSection === link.id ? "var(--accent)" : "var(--text-primary)",
                  fontSize: "1.05rem",
                  fontWeight: 500,
                  borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
                }}
              >
                {link.name}
              </a>
            ))}
            <div className="pt-2">
              <a
                href="#contact"
                onClick={closeMobile}
                className="btn-cyber-primary w-100"
                style={{ padding: "10px 16px", fontSize: "0.9rem" }}
              >
                Get In Touch
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
